import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import useReleaseResourceAllocationForRequest from '~/modules/resourcing/common/enhancers/requestStatusHandlers/useReleaseResourceAllocationForRequest';
import {
  useReleaseResourceAllocation,
  useToggleReleaseResourcesDialog
} from '~/modules/resourcing/common/hooks';
import useMeContext from '~/modules/me/useMeContext';
import { useProjectResourcePlanPermissions } from '~/modules/resourcing/hooks';
import { useResourceRequestToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import { getFilterInput } from './useCompletedProjectResourceAllocations';
import { useUpdateCompletedResourceAllocationProjectCacheOnDelete } from './useUpdateCompletedResourceAllocationCache';

const useResourceAllocationChartActions = ({ resourceAllocation, project }) => {
  const history = useHistory();
  const intl = useIntl();

  const {
    dateRange: chartDisplayDateRange,
    filter,
    sort
  } = useResourceRequestToolbarContext();

  const {
    releaseResourceAllocationsForRequest,
    releaseInProgress: releaseAllocationsForRequestLoading
  } = useReleaseResourceAllocationForRequest({ projectId: project.id });

  const {
    showReleaseResourcesDialog,
    openReleaseResourcesDialog,
    closeReleaseResourcesDialog
  } = useToggleReleaseResourcesDialog();

  const { permissionsMap, resourceCostMode } = useMeContext();

  const {
    isViewAdvancedSettingsEnabled,
    isViewAvailabilitySettingsEnabled
  } = useProjectResourcePlanPermissions(permissionsMap);

  const showTimeOff = Boolean(
    isViewAvailabilitySettingsEnabled && Boolean(chartDisplayDateRange)
  );

  const showHolidays =
    isViewAdvancedSettingsEnabled && Boolean(chartDisplayDateRange);

  const formattedDateRange = useMemo(
    () =>
      chartDisplayDateRange
        ? {
            startDate: dateToMidnightUTCString(chartDisplayDateRange.startDate),
            endDate: dateToMidnightUTCString(chartDisplayDateRange.endDate)
          }
        : null,
    [chartDisplayDateRange]
  );

  const {
    releaseResourceAllocation,
    loading: releaseAllocationLoading
  } = useReleaseResourceAllocation({
    resourceAllocation,
    updateDeleteCache: useUpdateCompletedResourceAllocationProjectCacheOnDelete(
      {
        projectUri: project.id,
        limit: 50,
        chartDateRange: formattedDateRange,
        showTimeOff,
        showHolidays,
        filter: getFilterInput(filter),
        sort,
        resourceCostMode
      }
    )
  });

  const openResourceRequestDialog = useCallback(
    () =>
      resourceAllocation.resourceRequestId &&
      history.push({
        hash: `#/resourceRequest/${resourceAllocation.resourceRequestId}`,
        state: {
          isProjectEditEnabled: false
        }
      }),
    [history, resourceAllocation]
  );

  const releaseResourcesAction = {
    label: intl.formatMessage({
      id: 'resourceRequestActions.releaseResource'
    }),
    onClick: openReleaseResourcesDialog
  };

  const viewResourceRequestAction = {
    label: intl.formatMessage({
      id: 'resourceRequestActions.viewResourceRequest'
    }),
    onClick: openResourceRequestDialog
  };

  const isEditProject = project.permittedActionUris.includes(
    'urn:replicon:project-action:edit-project'
  );

  const isEditTeam = project.permittedActionUris.includes(
    'urn:replicon:project-action:edit-team'
  );

  const actions = [
    ...(isEditProject && isEditTeam && resourceAllocation.user
      ? [releaseResourcesAction]
      : []),
    ...(resourceAllocation.resourceRequestId ? [viewResourceRequestAction] : [])
  ];

  return {
    actions,
    releaseAllocationLoading,
    releaseAllocationsForRequestLoading,
    releaseResourceAllocation,
    releaseResourceAllocationsForRequest,
    showReleaseResourcesDialog,
    closeReleaseResourcesDialog
  };
};

export default useResourceAllocationChartActions;
